import { useEffect, useRef, useState, useCallback } from "react";
import * as Sentry from "@sentry/browser";
import type { PlayerStatus, PlayerHookReturn } from "../types";
import { SquawkV2WebSocket } from "./SquawkV2WebSocket";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

// TODO: Replace with Keycloak token integration. Currently hardcoded for internal testing.
const V2_AUTH_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI3NmJiNmNjNy02YmM1LTQwMWUtOTkyNy1kOTQ5NjlhZTJjOTQiLCJyb2xlcyI6WyJsaXN0ZW4iLCJwdWJsaXNoIiwiYWRtaW4iXSwiZXhwIjoxNzc1ODIwODcyLCJpYXQiOjE3NzU3MzQ0NzIsImlzcyI6InNxdWF3ay1zdHViIn0.-fB3S2P0XXDfZ8WP48mmn5i9zfdY79AlNvhIduU_5T0";

const MAX_RETRIES = 5;
const BASE_RETRY_DELAY_MS = 1500;
const MAX_RETRY_DELAY_MS = 30000;

interface UseSquawkV2PlayerOptions {
  v2Keyword: string | null;
  enabled: boolean;
  authenticated: boolean;
}

function getRetryDelay(attempt: number): number {
  const exponential = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
  const capped = Math.min(exponential, MAX_RETRY_DELAY_MS);
  return Math.round(capped * (0.5 + Math.random() * 0.5));
}

function buildWsUrl(): string {
  const baseUrl = import.meta.env.VITE_AUDIO_V2_BASE_URL;
  if (!baseUrl) {
    console.error("[SquawkV2] VITE_AUDIO_V2_BASE_URL is not set");
    return "";
  }
  return `${baseUrl}/api/ws?token=${encodeURIComponent(V2_AUTH_TOKEN)}`;
}

/**
 * React hook for managing the v2 WebSocket + MSE audio player.
 *
 * Connects to the audio-v2 backend via WebSocket, subscribes to a keyword-based
 * channel, and plays streamed WebM/Opus audio through MediaSource Extensions.
 *
 * Returns the same { status, error, retryCount, setVolume } interface as
 * useWebRTCPlayer / useMSEPlayer / useShoutcastPlayer for drop-in compatibility.
 */
export const useSquawkV2Player = ({
  v2Keyword,
  enabled,
  authenticated,
}: UseSquawkV2PlayerOptions): PlayerHookReturn => {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const wsRef = useRef<SquawkV2WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionIdRef = useRef(0);

  const setVolume = useCallback((volume: number) => {
    wsRef.current?.set_volume(volume);
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.destroy();
      wsRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  // Connection effect
  useEffect(() => {
    if (!enabled || !authenticated || !v2Keyword) {
      setStatus("idle");
      setError(null);
      setRetryCount(0);
      cleanup();
      return;
    }

    const wsUrl = buildWsUrl();
    if (!wsUrl) {
      setStatus("error");
      setError(new Error("VITE_AUDIO_V2_BASE_URL is not configured"));
      return;
    }

    // Reset for new connection
    cleanup();

    connectionIdRef.current++;
    const thisConnectionId = connectionIdRef.current;

    console.log(`[SquawkV2 #${thisConnectionId}] Connecting for keyword: ${v2Keyword}`);
    diagnosticEventLog.record("squawk-v2:audio", "info", "connection-start", {
      connectionId: thisConnectionId,
      keyword: v2Keyword,
    });

    setStatus("connecting");
    setError(null);
    setRetryCount(0);

    // Create AudioContext — can throw NotSupportedError / SecurityError in
    // some browsers (older iOS Safari, locked-down enterprise policies).
    let audioContext: AudioContext;
    try {
      audioContext = new AudioContext();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[SquawkV2 #${thisConnectionId}] AudioContext creation failed:`, err);
      diagnosticEventLog.record("squawk-v2:audio", "error", "audio-context-failed", {
        connectionId: thisConnectionId,
        error: errMsg,
      });
      const diagnostics = getPlayerFailureDiagnostics(wsUrl, "v2", errMsg);
      Sentry.captureException(
        err instanceof Error ? err : new Error(`AudioContext creation failed: ${errMsg}`),
        {
          tags: buildPlayerFailureTags(
            "useSquawkV2Player",
            "v2_audio_context_creation_failed",
            "v2",
            diagnostics
          ),
          extra: {
            connectionId: thisConnectionId,
            wsUrl,
            networkDiagnostics: diagnostics.network,
          },
          fingerprint: ["useSquawkV2Player", "audio-context-creation-failed"],
        }
      );
      setStatus("error");
      setError(err instanceof Error ? err : new Error(errMsg));
      return;
    }
    audioContextRef.current = audioContext;

    // Create WebSocket client
    const ws = new SquawkV2WebSocket(wsUrl, audioContext);
    wsRef.current = ws;

    // Event handlers
    ws.on("connected", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      diagnosticEventLog.record("squawk-v2:audio", "info", "ws-connected", {
        connectionId: thisConnectionId,
      });
      // Subscribe to the channel keyword
      ws.subscribe([v2Keyword]);
    });

    ws.on("subscribed", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Subscribed to ${v2Keyword}`);
      diagnosticEventLog.record("squawk-v2:audio", "info", "subscribed", {
        connectionId: thisConnectionId,
        keyword: v2Keyword,
      });
      setStatus("connected");
      setError(null);
    });

    ws.on("stream_start", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Stream started`);
      // Resume AudioContext if suspended (autoplay policy)
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }
    });

    ws.on("error", (err: unknown) => {
      if (connectionIdRef.current !== thisConnectionId) return;
      const errMsg = typeof err === "string" ? err : "WebSocket error";
      console.error(`[SquawkV2 #${thisConnectionId}] Error:`, errMsg);
      diagnosticEventLog.record("squawk-v2:audio", "error", "error", {
        connectionId: thisConnectionId,
        error: errMsg,
      });
      setStatus("error");
      setError(new Error(errMsg));
    });

    ws.on("disconnected", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Disconnected`);
      diagnosticEventLog.record("squawk-v2:audio", "info", "disconnected", {
        connectionId: thisConnectionId,
      });
      // Don't set status here — the SquawkV2WebSocket handles auto-reconnect
      // Status will be updated via reconnecting event or error
    });

    ws.on("reconnecting", (attempt: unknown) => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Reconnecting (attempt ${attempt})`);
      setStatus("connecting");
      setRetryCount(typeof attempt === "number" ? attempt : 0);
    });

    // Connect
    ws.connect();

    return () => {
      cleanup();
      setStatus("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, authenticated, v2Keyword]);

  // Retry effect — handle errors that exceed the WebSocket's built-in reconnect
  useEffect(() => {
    if (status !== "error" || !error || !enabled || !v2Keyword) return;

    if (retryCount >= MAX_RETRIES) {
      console.error(`[SquawkV2] Max retries (${MAX_RETRIES}) reached, giving up`);
      diagnosticEventLog.record("squawk-v2:audio", "error", "max-retries-reached", {
        retryCount,
      });
      return;
    }

    const delay = getRetryDelay(retryCount);
    console.log(
      `[SquawkV2] Retrying in ${(delay / 1000).toFixed(1)}s (attempt ${retryCount + 1}/${MAX_RETRIES})`
    );

    retryTimeoutRef.current = setTimeout(() => {
      if (!enabled || !v2Keyword) return;

      setRetryCount((prev) => prev + 1);
      setStatus("connecting");
      setError(null);

      // Reconnect by triggering a new connection via cleanup + re-create
      cleanup();
      connectionIdRef.current++;
      const thisConnectionId = connectionIdRef.current;

      const wsUrl = buildWsUrl();
      if (!wsUrl) return;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const ws = new SquawkV2WebSocket(wsUrl, audioContext);
      wsRef.current = ws;

      ws.on("connected", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        ws.subscribe([v2Keyword]);
      });

      ws.on("subscribed", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("connected");
        setError(null);
      });

      ws.on("stream_start", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        if (audioContext.state === "suspended") audioContext.resume();
      });

      ws.on("error", (err: unknown) => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("error");
        setError(new Error(typeof err === "string" ? err : "WebSocket error"));
      });

      ws.on("reconnecting", (attempt: unknown) => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("connecting");
        setRetryCount(typeof attempt === "number" ? attempt : 0);
      });

      ws.connect();
    }, delay);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, error, retryCount, enabled, v2Keyword]);

  return {
    status,
    error,
    retryCount,
    setVolume,
  };
};
